import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useReview, useOverrideScreeningKnockout } from '../hooks/useApi';
import { ScoreGauge } from '../components/ScoreGauge';
import { RecommendationBadge } from '../components/StatusBadge';
import {
  ArrowLeft,
  CheckCircle2,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  User,
  Briefcase,
  FileText,
  Cpu,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { AIScreeningDetail } from '../components/AIScreeningAnalysis';

export default function ReviewDetail() {
  const { id } = useParams<{ id: string }>();
  const reviewId = parseInt(id || '0', 10);
  const { data: review, isLoading, error } = useReview(reviewId);
  const [showRawResponse, setShowRawResponse] = useState(false);
  const overrideMutation = useOverrideScreeningKnockout();

  // Parse strengths and concerns from JSON or comma-separated string
  const parseList = (data: string | null | undefined): string[] => {
    if (!data) return [];
    try {
      const parsed = JSON.parse(data);
      return Array.isArray(parsed) ? parsed : [data];
    } catch {
      return data.split(',').map((s) => s.trim()).filter(Boolean);
    }
  };

  const handleOverride = async () => {
    const reason = window.prompt('Enter override reason (optional):');
    if (reason === null) return; // user cancelled
    await overrideMutation.mutateAsync({ reviewId, overrideReason: reason || undefined });
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-center">
          <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600"></div>
          <p className="mt-2 text-gray-500 dark:text-gray-400">Loading review...</p>
        </div>
      </div>
    );
  }

  if (error || !review) {
    return (
      <div className="card dark:bg-gray-800 dark:border-gray-700 p-6">
        <div className="text-center">
          <p className="text-red-600">
            {error ? `Error loading review: ${error.message}` : 'Review not found'}
          </p>
          <Link to="/applications" className="btn btn-primary mt-4">
            Back to Applications
          </Link>
        </div>
      </div>
    );
  }

  const strengths = parseList(review.strengths);
  const concerns = parseList(review.concerns);

  return (
    <div className="space-y-6">
      {/* Header with Back Button */}
      <div className="flex items-center gap-4">
        <Link
          to={`/applications/${review.application.id}`}
          className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
        >
          <ArrowLeft className="h-5 w-5" />
          <span className="text-sm font-medium">Back to Application</span>
        </Link>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Left Column - Main Review Content */}
        <div className="space-y-6 lg:col-span-2">
          {/* Header Card */}
          <div className="card dark:bg-gray-800 dark:border-gray-700 p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  Candidate Review
                </h1>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Created {formatDistanceToNow(new Date(review.createdAt), { addSuffix: true })}
                </p>
                <div className="mt-4 flex items-center gap-3">
                  <RecommendationBadge level={review.recommendation} size="lg" />
                  {review.srReviewId && (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-green-100 dark:bg-green-900 px-3 py-1 text-xs font-medium text-green-800 dark:text-green-300">
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      Synced to SmartRecruiters
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Summary Card */}
          <div className="card dark:bg-gray-800 dark:border-gray-700 p-6">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">Summary</h2>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">{review.summary}</p>
          </div>

          {/* Strengths Card */}
          {strengths.length > 0 && (
            <div className="card dark:bg-gray-800 dark:border-gray-700 p-6">
              <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-green-700 dark:text-green-400">
                <CheckCircle2 className="h-5 w-5" />
                Strengths
              </h2>
              <ul className="space-y-3">
                {strengths.map((strength, index) => (
                  <li
                    key={index}
                    className="relative flex items-start gap-3 text-gray-700 dark:text-gray-300"
                  >
                    <span className="mt-1.5 flex h-2 w-2 flex-shrink-0 rounded-full bg-green-400"></span>
                    <span>{strength}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* AI Screening Analysis (US-110) */}
          <AIScreeningDetail
            rawResponse={review.rawResponse}
          />
          {/* Screening Knockout Override (US-111) */}
          {review.hasScreeningKnockout && (
            <div className="px-1">
              <button
                onClick={handleOverride}
                disabled={overrideMutation.isPending}
                className="text-xs text-orange-600 underline mt-2 disabled:opacity-50"
              >
                {overrideMutation.isPending
                  ? 'Overriding...'
                  : 'Override screening conflict (recruiter confirmed this is incorrect)'}
              </button>
              {overrideMutation.isSuccess && (
                <p className="text-xs text-green-600 mt-1">Override applied. Conflict cleared.</p>
              )}
            </div>
          )}

          {/* Concerns Card */}
          {concerns.length > 0 && (
            <div className="card dark:bg-gray-800 dark:border-gray-700 p-6">
              <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-amber-700 dark:text-amber-400">
                <AlertTriangle className="h-5 w-5" />
                Concerns
              </h2>
              <ul className="space-y-3">
                {concerns.map((concern, index) => (
                  <li
                    key={index}
                    className="relative flex items-start gap-3 text-gray-700 dark:text-gray-300"
                  >
                    <span className="mt-1.5 flex h-2 w-2 flex-shrink-0 rounded-full bg-amber-400"></span>
                    <span>{concern}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Raw AI Response Card */}
          <div className="card dark:bg-gray-800 dark:border-gray-700 overflow-hidden">
            <button
              onClick={() => setShowRawResponse(!showRawResponse)}
              className="flex w-full items-center justify-between p-6 text-left hover:bg-gray-50 dark:hover:bg-gray-750"
            >
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Raw AI Response
              </h2>
              {showRawResponse ? (
                <ChevronUp className="h-5 w-5 text-gray-400 dark:text-gray-500" />
              ) : (
                <ChevronDown className="h-5 w-5 text-gray-400 dark:text-gray-500" />
              )}
            </button>
            {showRawResponse && (
              <div className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 p-6">
                <pre className="overflow-x-auto rounded-lg bg-gray-900 p-4 text-xs text-gray-100">
                  {JSON.stringify(review.rawResponse, null, 2)}
                </pre>
              </div>
            )}
          </div>
        </div>

        {/* Right Column - Score & Links */}
        <div className="space-y-6">
          {/* Score Card */}
          <div className="card dark:bg-gray-800 dark:border-gray-700 p-6">
            <h2 className="mb-6 text-center text-lg font-semibold text-gray-900 dark:text-gray-100">
              Overall Score
            </h2>
            <div className="flex justify-center">
              <ScoreGauge score={parseFloat((review.overallScore / 10).toFixed(1))} maxScore={5} size="lg" />
            </div>
          </div>

          {/* Quick Links Card */}
          <div className="card dark:bg-gray-800 dark:border-gray-700 p-6">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
              Related
            </h2>
            <div className="space-y-3">
              <Link
                to={`/applications/${review.application.id}`}
                className="flex items-center gap-3 rounded-lg border border-gray-200 dark:border-gray-700 p-3 hover:bg-gray-50 dark:hover:bg-gray-750"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900">
                  <FileText className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    Application
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    ID: {review.application.id}
                  </div>
                </div>
              </Link>

              <Link
                to={`/people/${review.candidate.id}`}
                className="flex items-center gap-3 rounded-lg border border-gray-200 dark:border-gray-700 p-3 hover:bg-gray-50 dark:hover:bg-gray-750"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-100 dark:bg-purple-900">
                  <User className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {review.candidate.firstName} {review.candidate.lastName}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {review.candidate.email}
                  </div>
                </div>
              </Link>

              <Link
                to={`/jobs/${review.job.id}`}
                className="flex items-center gap-3 rounded-lg border border-gray-200 dark:border-gray-700 p-3 hover:bg-gray-50 dark:hover:bg-gray-750"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-100 dark:bg-green-900">
                  <Briefcase className="h-5 w-5 text-green-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                    {review.job.title}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {review.job.department || 'No department'}
                  </div>
                </div>
              </Link>
            </div>
          </div>

          {/* Metadata Card */}
          <div className="card dark:bg-gray-800 dark:border-gray-700 p-6">
            <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
              Metadata
            </h2>
            <dl className="space-y-2 text-sm">
              <div>
                <dt className="text-gray-500 dark:text-gray-400">Review ID</dt>
                <dd className="font-medium text-gray-900 dark:text-gray-100">{review.id}</dd>
              </div>
              <div>
                <dt className="text-gray-500 dark:text-gray-400">Created</dt>
                <dd className="font-medium text-gray-900 dark:text-gray-100">
                  {new Date(review.createdAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </dd>
              </div>
              <div>
                <dt className="text-gray-500 dark:text-gray-400">Last Updated</dt>
                <dd className="font-medium text-gray-900 dark:text-gray-100">
                  {new Date(review.updatedAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </dd>
              </div>
              {review.srReviewId && (
                <div>
                  <dt className="text-gray-500 dark:text-gray-400">SR Review ID</dt>
                  <dd className="font-mono text-xs font-medium text-gray-900 dark:text-gray-100 break-all">
                    {review.srReviewId}
                  </dd>
                </div>
              )}

              {/* AI Model Information (US-077) */}
              {review.modelUsed && (
                <>
                  <div className="pt-3 mt-3 border-t border-gray-200 dark:border-gray-700">
                    <dt className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-2">
                      AI Processing Details
                    </dt>
                  </div>
                  {/* Model Badge - "Generated by [model] (Tier)" format */}
                  <div className="mb-3">
                    <div className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-purple-50 to-blue-50 dark:bg-purple-900/30 dark:bg-blue-900/30 border border-purple-200 dark:border-purple-800 px-3 py-2">
                      <Cpu className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                      <div className="text-sm">
                        <span className="text-gray-600 dark:text-gray-400">Generated by </span>
                        <span className="font-mono font-medium text-gray-900 dark:text-gray-100">{review.modelUsed}</span>
                        {review.modelTier && (
                          <span className={`ml-2 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                            review.modelTier === 'premium' ? 'bg-amber-100 dark:bg-amber-900 text-amber-800 dark:text-amber-300' :
                            review.modelTier === 'standard' ? 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-300' :
                            'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-300'
                          }`}>
                            {review.modelTier.charAt(0).toUpperCase() + review.modelTier.slice(1)} tier
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  {review.costUsd && parseFloat(review.costUsd) > 0 && (
                    <div>
                      <dt className="text-gray-500 dark:text-gray-400">Processing Cost</dt>
                      <dd className="font-medium text-gray-900 dark:text-gray-100">
                        ${parseFloat(review.costUsd).toFixed(4)}
                      </dd>
                    </div>
                  )}
                  {(review.inputTokens || review.outputTokens) && (
                    <div>
                      <dt className="text-gray-500 dark:text-gray-400">Tokens</dt>
                      <dd className="font-medium text-gray-900 dark:text-gray-100">
                        {review.inputTokens?.toLocaleString() || 0} in / {review.outputTokens?.toLocaleString() || 0} out
                        {review.inputTokens && review.outputTokens && (
                          <span className="text-gray-500 dark:text-gray-400 ml-1">
                            ({(review.inputTokens + review.outputTokens).toLocaleString()} total)
                          </span>
                        )}
                      </dd>
                    </div>
                  )}
                  {review.processingTimeMs && (
                    <div>
                      <dt className="text-gray-500 dark:text-gray-400">Processing Time</dt>
                      <dd className="font-medium text-gray-900 dark:text-gray-100">
                        {review.processingTimeMs < 1000
                          ? `${review.processingTimeMs}ms`
                          : `${(review.processingTimeMs / 1000).toFixed(2)}s`
                        }
                      </dd>
                    </div>
                  )}
                </>
              )}
            </dl>
          </div>
        </div>
      </div>
    </div>
  );
}
